import { useMemo } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import FormControl from '@mui/material/FormControl';
import RadioGroup from '@mui/material/RadioGroup';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Chip from '@mui/material/Chip';
import {
  checkPreWritingAnswer,
  isTrendToFind,
  seriesLabelsFromTask,
} from '../outwrite/learnerPracticeUtils';
import { LEARNER, TREND_CHOICES } from '../outwrite/learnerTokens';

/** Mockup checkmark (option row / segment). */
function IconCheck({ size = 18, color = LEARNER.successIcon, label = 'Correct' }) {
  return (
    <Box
      component="svg"
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      aria-label={label}
      sx={{ flex: 'none', display: 'block' }}
    >
      <polyline
        points="5,12.5 10,17.5 19,7"
        stroke={color}
        strokeWidth="2.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </Box>
  );
}

/** Mockup cross (wrong selection). */
function IconCross({ size = 18, color = LEARNER.errorIcon, label = 'Your answer' }) {
  return (
    <Box
      component="svg"
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      aria-label={label}
      sx={{ flex: 'none', display: 'block' }}
    >
      <line x1="6" y1="6" x2="18" y2="18" stroke={color} strokeWidth="2.5" strokeLinecap="round" />
      <line x1="18" y1="6" x2="6" y2="18" stroke={color} strokeWidth="2.5" strokeLinecap="round" />
    </Box>
  );
}

/** Banner: circle + check. */
function BannerCheck() {
  return (
    <Box
      component="svg"
      width={24}
      height={24}
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden
      sx={{ flex: 'none' }}
    >
      <circle cx="12" cy="12" r="9" stroke={LEARNER.successIcon} strokeWidth="2.5" />
      <polyline
        points="8,12.5 11,15.5 16.5,9.5"
        stroke={LEARNER.successIcon}
        strokeWidth="2.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </Box>
  );
}

/** Banner: circle + exclamation. */
function BannerWarn() {
  return (
    <Box
      component="svg"
      width={24}
      height={24}
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden
      sx={{ flex: 'none' }}
    >
      <circle cx="12" cy="12" r="9" stroke={LEARNER.errorIcon} strokeWidth="2.2" />
      <line
        x1="12"
        y1="7.5"
        x2="12"
        y2="13"
        stroke={LEARNER.errorIcon}
        strokeWidth="2.2"
        strokeLinecap="round"
      />
      <circle cx="12" cy="16.5" r="0.6" fill={LEARNER.errorIcon} />
    </Box>
  );
}

function FeedbackBanner({ correct, title, explanation }) {
  return (
    <Box
      role="status"
      sx={{
        display: 'flex',
        gap: 1.5,
        alignItems: 'flex-start',
        p: 2,
        borderRadius: '12px',
        bgcolor: correct ? LEARNER.successContainer : LEARNER.errorContainer,
        color: correct ? LEARNER.onSuccessContainer : LEARNER.onErrorContainer,
      }}
    >
      {correct ? <BannerCheck /> : <BannerWarn />}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
        <Typography sx={{ fontSize: 16, fontWeight: 500 }}>{title}</Typography>
        {explanation ? (
          <Typography sx={{ fontSize: 16, lineHeight: '24px' }}>
            {explanation}
          </Typography>
        ) : null}
      </Box>
    </Box>
  );
}

/**
 * Connected pill segmented control (mockups 03/06).
 * After check: green = correct, red = wrong selection, with ✓ / ✕ icons.
 */
function TrendSegmentGroup({
  label,
  value,
  disabled,
  correctValue,
  showResult,
  onChange,
}) {
  return (
    <Box
      sx={{
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        gap: '12px 16px',
      }}
    >
      <Typography
        sx={{
          fontSize: 16,
          fontWeight: 500,
          minWidth: 96,
          color: LEARNER.onSurface,
        }}
      >
        {label}
      </Typography>
      <Box
        role="group"
        aria-label={label}
        sx={{
          display: 'flex',
          height: 40,
          border: `1px solid ${LEARNER.outline}`,
          borderRadius: '20px',
          overflow: 'hidden',
        }}
      >
        {TREND_CHOICES.map((c, index) => {
          const selected = value === c.value;
          const isCorrectChoice =
            showResult && correctValue != null && c.value === correctValue;
          const isWrongSelection =
            showResult && selected && correctValue != null && c.value !== correctValue;
          const isSoftCorrect =
            showResult && selected && correctValue == null;

          let bgcolor = LEARNER.surface;
          let color = LEARNER.onSurface;
          if (!showResult && selected) {
            bgcolor = LEARNER.primary;
            color = LEARNER.onPrimary;
          } else if (isCorrectChoice || isSoftCorrect) {
            bgcolor = LEARNER.successContainer;
            color = LEARNER.onSuccessContainer;
          } else if (isWrongSelection) {
            bgcolor = LEARNER.errorContainer;
            color = LEARNER.onErrorContainer;
          } else if (showResult) {
            color = '#75777F';
          }

          return (
            <Box
              key={c.value}
              component="button"
              type="button"
              disabled={disabled}
              aria-pressed={selected}
              onClick={() => onChange(c.value)}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                px: 2,
                border: 0,
                borderLeft: index === 0 ? 0 : `1px solid ${LEARNER.outline}`,
                bgcolor,
                color,
                fontFamily: 'Roboto, Arial, sans-serif',
                fontSize: 14,
                fontWeight: 500,
                whiteSpace: 'nowrap',
                cursor: disabled ? 'default' : 'pointer',
                m: 0,
                lineHeight: 1,
              }}
            >
              {isCorrectChoice || isSoftCorrect ? (
                <IconCheck />
              ) : null}
              {isWrongSelection ? <IconCross /> : null}
              {c.label}
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}

/**
 * One pre-writing question: radio from to_finds.options, or segmented
 * Up/Down/About the same per series for trend items (mockups 03/06).
 */
export default function OutwriteLearnerQuestion({
  task,
  toFind,
  questionIndex,
  questionCount,
  answer,
  result,
  seriesWords,
  onChangeAnswer,
  onCheck,
  onNext,
}) {
  const trend = isTrendToFind(toFind);
  const seriesLabels = useMemo(
    () => (trend ? seriesLabelsFromTask(task) : []),
    [trend, task],
  );
  const options = Array.isArray(toFind?.options) ? toFind.options : [];
  const checked = Boolean(result);
  const canCheck = trend
    ? seriesLabels.length > 0 &&
      seriesLabels.every((s) => answer?.selections?.[s])
    : Boolean(answer?.value);

  const handleRadio = (event) => {
    onChangeAnswer({ type: 'option', value: event.target.value });
  };

  const handleTrend = (series, value) => {
    if (value == null || checked) return;
    onChangeAnswer({
      type: 'segmented',
      selections: {
        ...(answer?.selections || {}),
        [series]: value,
      },
    });
  };

  const handleCheckClick = () => {
    const outcome = checkPreWritingAnswer(task, toFind, answer);
    onCheck(outcome);
  };

  const feedbackTitle = result?.correct
    ? 'Correct.'
    : result?.correctValue
      ? `Not quite – the answer is “${result.correctValue}”.`
      : result?.correctSelections
        ? (() => {
            const entries = Object.entries(result.correctSelections);
            if (entries.length === 1) {
              const [, dir] = entries[0];
              const label =
                TREND_CHOICES.find((c) => c.value === dir)?.label || dir;
              return `Not quite – the answer is “${label}”.`;
            }
            const wrong = seriesLabels.filter(
              (s) =>
                answer?.selections?.[s] &&
                result.correctSelections[s] &&
                answer.selections[s] !== result.correctSelections[s],
            );
            const nOk = seriesLabels.length - wrong.length;
            if (wrong.length === 1) {
              const s = wrong[0];
              const label =
                TREND_CHOICES.find((c) => c.value === result.correctSelections[s])
                  ?.label || result.correctSelections[s];
              return `${nOk} of ${seriesLabels.length} correct. ${s}: the answer is “${label}”.`;
            }
            return 'Not quite.';
          })()
        : 'Not quite.';

  return (
    <Box
      component="section"
      aria-label="Question"
      sx={{
        flex: '1 1 480px',
        minWidth: 0,
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
      }}
    >
      <Box
        sx={{
          bgcolor: LEARNER.surface,
          border: `1px solid ${LEARNER.outlineVariant}`,
          borderRadius: '12px',
          p: 3,
          display: 'flex',
          flexDirection: 'column',
          gap: 2.5,
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
          <Typography
            sx={{
              fontSize: 12,
              fontWeight: 500,
              letterSpacing: '0.5px',
              textTransform: 'uppercase',
              color: LEARNER.onSurfaceVariant,
            }}
          >
            Question {questionIndex + 1} of {questionCount}
          </Typography>
          <Typography
            component="h1"
            sx={{
              m: 0,
              fontSize: 22,
              lineHeight: '28px',
              fontWeight: 400,
              color: LEARNER.onSurface,
            }}
          >
            {toFind?.prompt || 'Question'}
          </Typography>
          {!trend && !checked ? (
            <Typography sx={{ fontSize: 14, color: LEARNER.onSurfaceVariant }}>
              Choose one answer.
            </Typography>
          ) : null}
        </Box>

        {trend && seriesLabels.length > 0 ? (
          <Stack spacing={2}>
            {seriesLabels.map((label) => (
              <TrendSegmentGroup
                key={label}
                label={label}
                value={answer?.selections?.[label] || null}
                disabled={checked}
                showResult={checked}
                correctValue={result?.correctSelections?.[label]}
                onChange={(v) => handleTrend(label, v)}
              />
            ))}
          </Stack>
        ) : (
          <FormControl component="fieldset" disabled={checked} sx={{ m: 0 }}>
            <RadioGroup
              name={toFind?.id || 'q'}
              value={answer?.value || ''}
              onChange={handleRadio}
              sx={{ gap: 0.5 }}
            >
              {options.map((opt) => {
                const selected = answer?.value === opt;
                const isCorrectOpt =
                  checked &&
                  result?.correctValue != null &&
                  opt === result.correctValue;
                const isWrongSel =
                  checked &&
                  selected &&
                  result?.correctValue != null &&
                  opt !== result.correctValue;
                const isSoftOk =
                  checked && selected && result?.correct && !result?.correctValue;

                let bgcolor = 'transparent';
                let color = LEARNER.onSurface;
                if (isCorrectOpt || isSoftOk) {
                  bgcolor = LEARNER.successContainer;
                  color = LEARNER.onSuccessContainer;
                } else if (isWrongSel) {
                  bgcolor = LEARNER.errorContainer;
                  color = LEARNER.onErrorContainer;
                } else if (checked) {
                  color = '#75777F';
                }

                return (
                  <Box
                    key={opt}
                    component="label"
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 2,
                      minHeight: 56,
                      px: 2,
                      borderRadius: '8px',
                      fontSize: 16,
                      bgcolor,
                      color,
                      cursor: checked ? 'default' : 'pointer',
                    }}
                  >
                    <Box
                      component="input"
                      type="radio"
                      name={toFind?.id || 'q'}
                      value={opt}
                      checked={selected}
                      disabled={checked}
                      onChange={handleRadio}
                      sx={{
                        accentColor: LEARNER.primary,
                        width: 20,
                        height: 20,
                        m: 0,
                        flex: 'none',
                      }}
                    />
                    <Typography
                      component="span"
                      sx={{ fontSize: 16, color: 'inherit', flex: 1 }}
                    >
                      {opt}
                    </Typography>
                    {isCorrectOpt || isSoftOk ? <IconCheck size={20} /> : null}
                    {isWrongSel ? <IconCross size={20} /> : null}
                  </Box>
                );
              })}
            </RadioGroup>
          </FormControl>
        )}

        {result ? (
          <FeedbackBanner
            correct={result.correct}
            title={feedbackTitle}
            explanation={result.explanation}
          />
        ) : null}

        {result && Array.isArray(seriesWords) && seriesWords.length > 0 ? (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 1.5,
              pt: 0.5,
              borderTop: `1px solid ${LEARNER.outlineVariantSoft}`,
            }}
          >
            <Typography
              sx={{
                fontSize: 14,
                fontWeight: 500,
                color: LEARNER.onSurfaceVariant,
                pt: 1.5,
              }}
            >
              Words for this line
            </Typography>
            <Stack direction="row" flexWrap="wrap" gap={1}>
              {seriesWords.map((w) => (
                <Chip
                  key={w}
                  label={w}
                  size="small"
                  sx={{
                    height: 32,
                    borderRadius: '8px',
                    border: `1px solid ${LEARNER.outlineVariant}`,
                    bgcolor: LEARNER.surface,
                    fontWeight: 500,
                    fontSize: 14,
                  }}
                />
              ))}
            </Stack>
          </Box>
        ) : null}

        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          {!checked ? (
            <Button
              variant="contained"
              disableElevation
              disabled={!canCheck}
              onClick={handleCheckClick}
              sx={{
                height: 40,
                px: 3,
                borderRadius: '20px',
                textTransform: 'none',
                fontWeight: 500,
                bgcolor: LEARNER.primary,
                '&:hover': { bgcolor: '#1D4785' },
                '&.Mui-disabled': {
                  bgcolor: LEARNER.disabled,
                  color: LEARNER.onDisabled,
                },
              }}
            >
              Check answer
            </Button>
          ) : (
            <Button
              variant="contained"
              disableElevation
              onClick={onNext}
              sx={{
                height: 40,
                px: 3,
                borderRadius: '20px',
                textTransform: 'none',
                fontWeight: 500,
                bgcolor: LEARNER.primary,
                '&:hover': { bgcolor: '#1D4785' },
              }}
            >
              Next question
            </Button>
          )}
        </Box>
      </Box>
    </Box>
  );
}
